import React, { useState } from 'react'
import { Modal, useModal } from '@cjdev-internal/visual-stack-x/Modal'
import { Button } from '@cjdev-internal/visual-stack-x/Button'
import { Note } from '@cjdev-internal/visual-stack-x/Note'
import { useAlert } from '@cjdev-internal/visual-stack-x/Alert'
import { FormattedMessage, useIntl, useNavigate } from '@platform/cj-platform-navigation'
import useApi from '../../PlatformApi'
import styles from './AddAccountModal.module.css'
import { Text } from '@cjdev-internal/visual-stack-x/Text'
import { Row } from '@cjdev-internal/visual-stack-x/Row'
import { Field } from '@cjdev-internal/visual-stack-x/Field'
import { Input } from '@cjdev-internal/visual-stack-x/Input'
import { Stack } from '@cjdev-internal/visual-stack-x/Stack'
import { Link } from '@cjdev-internal/visual-stack-x/Link'
export const AddAccountModal = ({ user }) => {
  const [alert, showAlert, closeAlert] = useAlert()
  const [modal, openModal, closeModal] = useModal()
  const openModalAccount = () =>
    openModal(
      <ModalAccount
        closeModal={closeModal}
        user={user}
        showAlert={showAlert}
        closeAlert={closeAlert}
      />
    )

  return (
    <>
      {modal}
      <Button type="primary" onClick={openModalAccount}>
        <FormattedMessage defaultMessage="Add Account" />
      </Button>
      {alert}
    </>
  )
}

const ModalAccount = ({ closeModal, user, showAlert, closeAlert }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const intl = useIntl()
  const api = useApi()
  const navigate = useNavigate()

  const emailInputChangeHandler = (event) => {
    setEmail(event.target.value)
  }

  const passwordInputChangeHandler = (event) => {
    setPassword(event.target.value)
  }

  const pubSuperUser =
    user?.currentCompany.type === 'publisher' && user?.currentCompany.companyRole === 'SUPERUSER'

  const emailLabel = pubSuperUser ? (
    <FormattedMessage defaultMessage="Superuser Email" />
  ) : (
    <FormattedMessage defaultMessage="Email" />
  )
  const passwordLabel = pubSuperUser ? (
    <FormattedMessage defaultMessage="Superuser Password" />
  ) : (
    <FormattedMessage defaultMessage="Password" />
  )

  function closeAccountModal() {
    setEmail('')
    setPassword('')
    setErrorMessage('')
    closeModal()
  }

  function linkAccount() {
    if (user?.currentCompany.type === 'advertiser')
      return api.putLinkAdvertisers(user.userId, { username: email, password })
    else if (user?.currentCompany.type === 'publisher')
      return api.putLinkPublishers(user.userId, {
        username: email,
        password
      })
  }

  const formSubmissionHandler = (event) => {
    event.preventDefault()
    return linkAccount()
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = ''
          const contentType = response.headers?.get('content-type')
            ? response.headers?.get('content-type')
            : ''
          if (contentType && contentType.indexOf('text/plain') !== -1) {
            errorMessage = await response.text()
          }
          throw new Error(
            errorMessage !== ''
              ? errorMessage
              : intl.formatMessage({ defaultMessage: 'Failed to add account.' })
          )
        } else {
          return response.json()
        }
      })
      .then(({ id, name }) => {
        showAlert({
          type: 'success',
          closeType: 'icon',
          children: (
            <FormattedMessage
              defaultMessage="Account {id} - {name} successfully added. It may take up to a minute to display in the platform."
              values={{ id, name }}
            />
          ),
          onClose: closeAlert
        })
        closeAccountModal()
      })
      .catch((err) => {
        setErrorMessage(err.message)
      })
  }

  const demoModalProps = {
    width: '360px',
    onBackgroundClick: closeModal,
    onEscapeKeyUp: closeModal,
    headerTitle: (
      <Text size={16} weight={'medium'}>
        <FormattedMessage defaultMessage="Add Account" />
      </Text>
    ),
    footer: (
      <Row gap={'medium'} justify={'end'}>
        <Button type="tertiary" onClick={closeModal}>
          <FormattedMessage defaultMessage="Cancel" />
        </Button>
        <Button type="primary" onClick={formSubmissionHandler} data-testid="add-button">
          <FormattedMessage defaultMessage="Add" />
        </Button>
      </Row>
    )
  }

  return (
    <Modal {...demoModalProps}>
      {pubSuperUser ? (
        <div className={styles.modalAccountTitlePub}>
          <Note iconSize={20} type={'info'} className={styles.modalAccountTitle}>
            <FormattedMessage defaultMessage="To add an account, you need to use the superuser login credentials of the account you want to link." />
            &nbsp;
            <Link
              target="_self"
              rel="noopener noreferrer"
              onClick={() => navigate('/support-center?id=kA1150000009LQR')}
            >
              <FormattedMessage defaultMessage="Learn More" />
            </Link>
          </Note>
        </div>
      ) : (
        <div className={styles.modalAccountTitle}>
          <FormattedMessage defaultMessage="Enter credentials to link accounts" />
        </div>
      )}
      {errorMessage && (
        <div className={styles.modalAccountErrorMessage}>
          <Note type="warning">{errorMessage}</Note>
        </div>
      )}
      <form>
        <Stack gap={'medium'}>
          <Field label={emailLabel}>
            <Input
              id="add-account-email"
              type="email"
              onChange={emailInputChangeHandler}
              value={email}
            />
          </Field>
          <Field label={passwordLabel}>
            <Input
              id="add-account-password"
              type="password"
              onChange={passwordInputChangeHandler}
              value={password}
              autoComplete={''}
            />
          </Field>
        </Stack>
      </form>
    </Modal>
  )
}
